import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { StockMovement, StockMovementParams, StockInDTO, StockOutDTO, StockAdjustmentDTO } from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';

export function useStockMovements(consumableId?: string, initialParams?: StockMovementParams) {
  const { inventoryRepo } = useServices();
  const [data, setData] = useState<PaginatedResponse<StockMovement> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<StockMovementParams>(initialParams || { page: 1, limit: 20 });

  const fetchMovements = useCallback(async () => {
    if (!consumableId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryRepo.getStockMovements(consumableId, params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar movimientos de stock');
    } finally {
      setLoading(false);
    }
  }, [inventoryRepo, consumableId, params]);

  useEffect(() => {
    if (consumableId) {
      fetchMovements();
    }
  }, [fetchMovements, consumableId]);

  const filterByType = useCallback((type: StockMovementParams['type']) => {
    setParams(prev => ({ ...prev, type, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const stockIn = useCallback(async (targetConsumableId: string, data: StockInDTO) => {
    const movement = await inventoryRepo.stockIn(targetConsumableId, data);
    if (consumableId === targetConsumableId) {
      fetchMovements();
    }
    return movement;
  }, [inventoryRepo, consumableId, fetchMovements]);

  const stockOut = useCallback(async (targetConsumableId: string, data: StockOutDTO) => {
    const movement = await inventoryRepo.stockOut(targetConsumableId, data);
    if (consumableId === targetConsumableId) {
      fetchMovements();
    }
    return movement;
  }, [inventoryRepo, consumableId, fetchMovements]);

  const adjustStock = useCallback(async (targetConsumableId: string, data: StockAdjustmentDTO) => {
    const movement = await inventoryRepo.adjustStock(targetConsumableId, data);
    if (consumableId === targetConsumableId) {
      fetchMovements();
    }
    return movement;
  }, [inventoryRepo, consumableId, fetchMovements]);

  return {
    movements: data?.data || [],
    pagination: data?.meta,
    loading,
    error,
    params,
    filterByType,
    goToPage,
    stockIn,
    stockOut,
    adjustStock,
    refresh: fetchMovements,
  };
}
